import { inject } from "vue";

/*
 * Composable to send an action event to invoke an action handler.
 * The chat message action state in `ChatDetail` is updated when the
 * action is resolved.
 *
 * If `sync` is passed to the `execute` handler, the action handler
 * is running synchronously so that the action handler payload
 * or errors are returned to the caller.
 *
 * Pass `streaming: true` to route through the SSE streaming path when available,
 * so that server-sent messages are delivered inline. Only use this for actions
 * that push new messages back (e.g. reassign).
 * Pass `optimistic: false` when hiding the action before server confirmation would
 * make a failure impossible to show or retry.
 */
export function useActionHandler() {
    const { $sendEvent } = inject("globalProperties");
    const updateActionState = inject("updateActionState");
    const sendStreamingEvent = inject("sendStreamingEvent", null);

    async function execute(actionType, payload, { sync = false, streaming = false, optimistic = true } = {}) {
        if (optimistic) {
            updateActionState(payload);
        }
        if (streaming && sendStreamingEvent) {
            await sendStreamingEvent("action", { data: { type: actionType, payload, sync } });
            return;
        }
        const result = await $sendEvent("action", { data: { type: actionType, payload, sync } });
        return result;
    }

    return {
        execute,
    };
}
